import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import Request from "../Utils/Request/Request";
import { showToast } from "../Utils";

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

function TemplateImages() {
  const inputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [previewImage, setPreviewImage] = useState(null);

  const getImageUrl = (image) =>
    `${API_URL}/image-storage/${encodeURIComponent(image.filename)}`;

  const filteredImages = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return images;
    return images.filter((image) =>
      String(image.originalName || image.filename || "").toLocaleLowerCase().includes(query)
    );
  }, [images, search]);

  const paginatedImages = useMemo(
    () => filteredImages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredImages, page, rowsPerPage]
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredImages.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredImages.length, page, rowsPerPage]);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Request.get("/template-images");
      setImages(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not load template images.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleFiles = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    const invalidFile = selectedFiles.find((file) => !ACCEPTED_IMAGE_TYPES.includes(file.type));
    if (invalidFile) {
      showToast("error", `${invalidFile.name} is not a supported image type.`);
      return;
    }

    if (!selectedFiles.length) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    setUploading(true);
    setError("");
    try {
      const response = await Request.post("/template-images", formData);
      setImages((current) => [...response.data, ...current]);
      showToast("success", `${response.data.length} image${response.data.length === 1 ? "" : "s"} uploaded.`);
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Could not upload the selected images.";
      setError(message);
      showToast("error", message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (image) => {
    if (!window.confirm(`Delete ${image.originalName}?`)) return;

    setDeletingId(image.id);
    setError("");
    try {
      await Request.delete(`/template-images/${image.id}`);
      setImages((current) => current.filter((item) => item.id !== image.id));
      showToast("success", "Template image deleted.");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Could not delete the template image.";
      setError(message);
      showToast("error", message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ px: 5 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 220,
          p: 4,
          border: "2px dashed #9eb0b8",
          borderRadius: 2,
          backgroundColor: "#fafcfc",
          textAlign: "center",
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "#365b6d", mb: 1 }} />
        <Typography variant="h6" sx={{ color: "#365b6d", fontWeight: 700 }}>
          Upload template images
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}>
          PNG, JPG, WebP, or SVG files · up to 5 MB each
        </Typography>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          onChange={handleFiles}
        />
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadOutlinedIcon />}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          sx={{ backgroundColor: "#365b6d", "&:hover": { backgroundColor: "#274653" } }}
        >
          {uploading ? "Uploading…" : "Select Images"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {!loading && images.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder="Search template images"
            inputProps={{ "aria-label": "Search template images" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 320 } }}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#365b6d" }} />
        </Box>
      ) : images.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>
          No template images uploaded yet.
        </Typography>
      ) : filteredImages.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>
          No template images match your search.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 2,
              mt: 3,
            }}
          >
            {paginatedImages.map((image) => (
              <Box
                key={image.id}
                sx={{ position: "relative", overflow: "hidden", border: "1px solid #d9e0e4", borderRadius: 2 }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={`Preview ${image.originalName}`}
                  onClick={() => setPreviewImage(image)}
                  sx={{
                    position: "relative",
                    display: "block",
                    width: "100%",
                    p: 0,
                    border: 0,
                    cursor: "zoom-in",
                    backgroundColor: "#f4f6f7",
                    "&:hover .template-image-zoom": { opacity: 1 },
                    "&:focus-visible .template-image-zoom": { opacity: 1 },
                    "&:focus-visible": { outline: "3px solid #365b6d", outlineOffset: -3 },
                  }}
                >
                  <Box
                    component="img"
                    src={getImageUrl(image)}
                    alt={image.originalName}
                    sx={{ display: "block", width: "100%", height: 150, objectFit: "contain" }}
                  />
                  <Box
                    className="template-image-zoom"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      backgroundColor: "rgba(20, 42, 51, .5)",
                      opacity: 0,
                      transition: "opacity .2s",
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: 38 }} />
                  </Box>
                </Box>
                <Typography variant="caption" title={image.originalName} sx={{ display: "block", p: 1.25, pr: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {image.originalName}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Delete ${image.originalName}`}
                  disabled={deletingId === image.id}
                  onClick={() => removeImage(image)}
                  sx={{ position: "absolute", right: 6, bottom: 5, color: "#c62828" }}
                >
                  {deletingId === image.id ? <CircularProgress size={18} /> : <DeleteOutlineIcon fontSize="small" />}
                </IconButton>
              </Box>
            ))}
          </Box>
          <TablePagination
            component="div"
            count={filteredImages.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[6, 12, 24]}
          />
        </>
      )}

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, pr: 1 }}>
          <Typography component="span" noWrap title={previewImage?.originalName} sx={{ flex: 1, fontWeight: 700 }}>
            {previewImage?.originalName}
          </Typography>
          <IconButton aria-label="Close preview" onClick={() => setPreviewImage(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#eef2f3" }}>
          {previewImage && (
            <Box
              component="img"
              src={getImageUrl(previewImage)}
              alt={previewImage.originalName}
              sx={{ display: "block", maxWidth: "100%", maxHeight: "calc(92vh - 100px)", objectFit: "contain", backgroundColor: "#fff" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TemplateImages;
